'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, Edit2, Trash2, FileText, Calendar } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { purchaseOrders, suppliers } from '../data/mock-data';
import { formatCurrency } from '../../../types';
import { purchaseOrderStatusConfig, type PurchaseOrderStatus } from '../types';
import type { PurchaseOrder } from '../types';

// 발주서 작성 모달
function PurchaseOrderAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [items, setItems] = useState([{ itemName: '', quantity: 1, unitPrice: 0 }]);

  const addItem = () => setItems([...items, { itemName: '', quantity: 1, unitPrice: 0 }]);
  const removeItem = (index: number) => {
    if (items.length > 1) setItems(items.filter((_, i) => i !== index));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="발주서 작성"
      description="새로운 구매 발주서를 작성합니다."
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button variant="outline">초안 저장</Button>
          <Button onClick={onClose}>발주</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="발주번호" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="공급업체" required>
            <FormSelect>
              <option value="">공급업체를 선택하세요</option>
              {suppliers.filter(s => s.isActive).map((supplier) => (
                <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="발주일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="납기요청일" required>
            <FormInput type="date" />
          </FormField>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">발주 품목</h3>
            <Button variant="outline" size="sm" onClick={addItem}>
              <Plus className="w-4 h-4 mr-1" />품목 추가
            </Button>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">품목</th>
                  <th className="text-right p-3 font-medium w-24">수량</th>
                  <th className="text-right p-3 font-medium w-32">단가</th>
                  <th className="text-right p-3 font-medium w-32">금액</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((_, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-2">
                      <FormSelect>
                        <option value="">품목 선택</option>
                        <option value="mat-001">원지 (A등급)</option>
                        <option value="mat-002">원지 (B등급)</option>
                        <option value="mat-003">골심지</option>
                        <option value="mat-004">접착제</option>
                      </FormSelect>
                    </td>
                    <td className="p-2"><FormInput type="number" min={1} defaultValue={1} className="text-right" /></td>
                    <td className="p-2"><FormInput type="number" min={0} defaultValue={0} className="text-right" /></td>
                    <td className="p-2 text-right font-medium">₩0</td>
                    <td className="p-2">
                      <button onClick={() => removeItem(index)} className="p-1 rounded hover:bg-accent" disabled={items.length === 1}>
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end">
          <div className="w-64 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">소계</span><span>₩0</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">부가세 (10%)</span><span>₩0</span></div>
            <div className="flex justify-between pt-2 border-t border-border font-bold text-base"><span>합계</span><span>₩0</span></div>
          </div>
        </div>

        <FormField label="비고">
          <FormTextarea rows={2} placeholder="발주 관련 메모를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function PurchaseOrderListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return purchaseOrders.filter((order) => {
      // 검색 필터
      const matchesSearch =
        order.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.supplierName.toLowerCase().includes(searchQuery.toLowerCase());

      // 상태 필터
      const matchesStatus =
        selectedStatus === 'all' || order.status === selectedStatus;

      // 공급업체 필터
      const matchesSupplier =
        selectedSupplier === 'all' || order.supplierId === selectedSupplier;

      return matchesSearch && matchesStatus && matchesSupplier;
    });
  }, [searchQuery, selectedStatus, selectedSupplier]);

  const columns = [
    {
      key: 'code',
      label: '발주번호',
      width: '12%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm text-primary">{String(value)}</span>
      ),
    },
    {
      key: 'supplierName',
      label: '공급업체',
      width: '16%',
      sortable: true,
      render: (value: unknown) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <FileText className="w-4 h-4 text-primary" />
          </div>
          <span className="font-medium truncate">{String(value)}</span>
        </div>
      ),
    },
    {
      key: 'orderDate',
      label: '발주일',
      width: '12%',
      sortable: true,
      render: (value: unknown, row: PurchaseOrder) => (
        <div>
          <div className="flex items-center gap-1 text-sm">
            <Calendar className="w-3 h-3 text-muted-foreground flex-shrink-0" />
            <span className="whitespace-nowrap">{String(value)}</span>
          </div>
          <div className="text-xs text-muted-foreground whitespace-nowrap">
            납기: {row.requiredDate}
          </div>
        </div>
      ),
    },
    {
      key: 'totalQty',
      label: '수량',
      width: '10%',
      align: 'right' as const,
      render: (value: unknown, row: PurchaseOrder) => (
        <div>
          <div className="font-medium whitespace-nowrap">{String(value)}</div>
          {row.receivedQty > 0 && (
            <div className="text-xs text-muted-foreground whitespace-nowrap">
              입고: {row.receivedQty}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'grandTotal',
      label: '금액',
      width: '14%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown, row: PurchaseOrder) => (
        <div>
          <div className="font-medium whitespace-nowrap">{formatCurrency(value as number)}</div>
          {row.billedAmount > 0 && (
            <div className="text-xs text-muted-foreground whitespace-nowrap">
              청구: {formatCurrency(row.billedAmount)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      label: '상태',
      width: '8%',
      render: (value: unknown) => {
        const status = value as PurchaseOrderStatus;
        const config = purchaseOrderStatusConfig[status];
        const colorClasses: Record<string, string> = {
          gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
          blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
          yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
          orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
          green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        };
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colorClasses[config.color]}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '10%',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="보기">
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="수정">
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="삭제">
            <Trash2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Record<string, number> = {};
    purchaseOrders.forEach((order) => {
      stats[order.status] = (stats[order.status] || 0) + 1;
    });
    return stats;
  }, []);

  // 요약 정보
  const totalOrders = purchaseOrders.length;
  const totalAmount = purchaseOrders
    .filter((o) => o.status !== 'cancelled' && o.status !== 'draft')
    .reduce((sum, o) => sum + o.grandTotal, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="발주서 관리"
        description="구매 발주서를 조회하고 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            발주서 작성
          </button>
        }
      />

      {/* 상태별 요약 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(purchaseOrderStatusConfig).map(([status, config]) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(selectedStatus === status ? 'all' : status)}
            className={`p-3 rounded-lg border text-left transition-colors ${
              selectedStatus === status
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:bg-accent/50'
            }`}
          >
            <div className="text-xs text-muted-foreground">{config.label}</div>
            <div className="text-xl font-bold mt-1">
              {statusStats[status] || 0}건
            </div>
          </button>
        ))}
      </div>

      {/* 요약 정보 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">전체 발주서</div>
          <div className="text-2xl font-bold mt-1">{totalOrders}건</div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">총 발주금액</div>
          <div className="text-2xl font-bold mt-1">{formatCurrency(totalAmount)}</div>
        </div>
      </div>

      {/* 필터 영역 */}
      <div className="flex flex-wrap gap-4">
        {/* 검색 */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="발주번호, 공급업체 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* 상태 필터 */}
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 상태</option>
          {Object.entries(purchaseOrderStatusConfig).map(([status, config]) => (
            <option key={status} value={status}>
              {config.label}
            </option>
          ))}
        </select>

        {/* 공급업체 필터 */}
        <select
          value={selectedSupplier}
          onChange={(e) => setSelectedSupplier(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 공급업체</option>
          {suppliers
            .filter((s) => s.isActive)
            .map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
        </select>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 발주서 작성 모달 */}
      <PurchaseOrderAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
